import { useRef, useState, useCallback } from "react";
import { fetchWithAuth } from "../../../../api-client/api_client";
import { APIEndpointMethod } from "../../../../models/Entities";
import { showErrorToast } from "../../../shared/Toasts";
import {
  renderCustomComponent,
  toggleInputDisabled,
  toggleMsgLoader,
} from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/ChatWidget/api";
import { MarkdownMessage } from "../components/MarkdownMessage";
import { Integration } from "../../../../models/Entities";

interface IntegrationConversationResponse {
  response_id: string;
  session_id?: string;
  content: string;
}

export function useIntegrationConversation(integration: Integration) {
  // ref for previous response ID
  const prevIdRef = useRef<string | null>(null);
  // state for session ID
  const [sessionId, setSessionId] = useState<string | null>(null);
  // state for loading
  const [loading, setLoading] = useState(false);

  // helper to toggle both input & loader
  const toggleChatControls = () => {
    toggleInputDisabled();
    toggleMsgLoader();
  };

  const send = useCallback(
    (text: string) => {
      setLoading(true);
      toggleChatControls();

      fetchWithAuth({
        path: "/ai/blueprint-editor/int-conversation/",
        method: APIEndpointMethod.POST,
        headers: { "Content-Type": "application/json" },
        body: {
          prev_response_id: prevIdRef.current,
          session_id: sessionId,
          integration_id: integration.id,
          prompt: text,
        },
        onResponse: (data: IntegrationConversationResponse) => {
          setLoading(false);
          toggleChatControls();
          prevIdRef.current = data.response_id;
          setSessionId(data.session_id ?? null);
          renderCustomComponent(MarkdownMessage, {
            text: data.content,
            response_id: data.response_id,
            id: data.response_id,
          });
        },
        onError: (error) => {
          setLoading(false);
          toggleChatControls();
          error
            ?.json()
            .then((value: string | Record<string, any>) => {
              let errorMessage = "";
              if (typeof value == "string") {
                errorMessage = value;
              } else {
                errorMessage = value?.non_field_errors ?? JSON.stringify(value);
              }
              showErrorToast(`Failed to send message. Error message: \n ${errorMessage}`);
            })
            .catch(() => {
              showErrorToast("Failed to send message");
            });
        },
      });
    },
    [integration, sessionId]
  );

  return { send, loading };
}
